import { html, View } from 'rune-ts';
import klass from './SeeMoreSettingButton.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { pipe, map, toArray } from '@fxts/core';
import { shareFont } from '@services/MarppleShop/shared/typography';

export interface SeeMoreSettingItem {
  key: string;
  label: string;
  font_klass?: string;

  // eslint-disable-next-line no-use-before-define
  onClick?: (view: SeeMoreSettingButton) => void | Promise<unknown>;
}

export interface SeeMoreSettingButtonProps {
  items: Array<SeeMoreSettingItem>;
}

export class SeeMoreSettingButton extends View<SeeMoreSettingButtonProps> {
  private show?: boolean;

  private documentClickHandler?: (event: Event) => void;

  protected override template() {
    return html`
      <div class="${klass.see_more_setting_button}">
        <button class="${klass.button}" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 4.8999C10.6904 4.8999 11.25 4.34026 11.25 3.6499C11.25 2.95955 10.6904 2.3999 10 2.3999C9.30964 2.3999 8.75 2.95955 8.75 3.6499C8.75 4.34026 9.30964 4.8999 10 4.8999Z"
              fill="currentColor"
            />
            <path
              d="M10 11.2499C10.6904 11.2499 11.25 10.6903 11.25 9.9999C11.25 9.30955 10.6904 8.7499 10 8.7499C9.30964 8.7499 8.75 9.30955 8.75 9.9999C8.75 10.6903 9.30964 11.2499 10 11.2499Z"
              fill="currentColor"
            />
            <path
              d="M10 17.5999C10.6904 17.5999 11.25 17.0403 11.25 16.3499C11.25 15.6595 10.6904 15.0999 10 15.0999C9.30964 15.0999 8.75 15.6595 8.75 16.3499C8.75 17.0403 9.30964 17.5999 10 17.5999Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <div
          class="${classes(klass.see_more_settings, {
            [klass.show]: Boolean(this.show),
          })}"
        >
          <ul class="${classes(klass.items)}">
            ${pipe(
              this.data.items,
              map(
                (item) => html`
                  <li class="${classes(klass.item)}" data-key="${item.key}">
                    <button
                      class="${classes(shareFont('14_medium'), {
                        [String(item?.font_klass)]: Boolean(item.font_klass),
                      })}"
                      type="button"
                    >
                      ${item.label}
                    </button>
                  </li>
                `,
              ),
              toArray,
            )}
          </ul>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.bindEvents();
  }

  protected override onUnmount() {
    this.unbindEvents();
  }

  private bindEvents() {
    this.unbindEvents();

    this.documentClickHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      const itemsContainer = this.element().querySelector(`.${klass.items}`);

      if (itemsContainer && !itemsContainer.contains(target) && this.show) {
        this.setItemsShow(false);
      }
    };

    document.addEventListener('click', this.documentClickHandler);

    this.delegate('click', `.${klass.button}`, (event) => {
      event.originalEvent.stopPropagation();
      event.originalEvent.preventDefault();
      this.setItemsShow(!this.show);
    });

    this.delegate('click', `.${klass.item}`, async (event) => {
      const target = event.currentTarget as HTMLElement;
      const key = target.dataset.key;
      const selected = this.data.items.find((item) => item.key === key);

      if (selected) await selected.onClick?.(this);
    });
  }

  private unbindEvents() {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = undefined;
    }
  }

  public setItemsShow(show: boolean) {
    this.show = show;
    this.element().querySelector(`.${klass.see_more_settings}`)?.classList.toggle(klass.show, this.show);
  }
}
